import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, doc, updateDoc, orderBy } from './firestore-compat';
import { ArquivoLink } from './arquivos';

const getLiberacaoStatus = (prof) => {
  if (!prof?.liberacaoTemporaria) return null;
  if (prof.statusRH === 'Aprovado' && prof.statusTI === 'Criado') return null;
  const grantedAt = new Date(prof.liberacaoTemporariaEm);
  if (isNaN(grantedAt.getTime())) return { ativa: true, diasRestantes: 30 };
  const expiresAt = new Date(grantedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diasRestantes = Math.ceil((expiresAt - now) / 86400000);
  return { ativa: now < expiresAt, diasRestantes, expiresAt };
};

export default function PortalRH() {
  const currentUser = auth.currentUser;
  const [profissionais, setProfissionais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('aguardando'); // 'aguardando' | 'aprovados' | 'recusados'

  // Filtros da tabela
  const [filterNome, setFilterNome] = useState('');
  const [filterEspecialidade, setFilterEspecialidade] = useState('');
  const [filterCooperativa, setFilterCooperativa] = useState('');

  // Modal Doc State
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedProf, setSelectedProf] = useState(null);
  const [validadeFilter, setValidadeFilter] = useState('todos'); // 'todos' | 'vencidos' | 'vencendo' | 'vacinas'

  useEffect(() => {
    const q = query(collection(db, 'profissionais_parceiros'), orderBy('criadoEm', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setProfissionais(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleMudarStatusRH = async (p, novoStatus) => {
    let motivo = '';
    if (novoStatus === 'Reprovado') {
      motivo = window.prompt(`Qual o motivo da reprovação de ${p.nome}? (A cooperativa verá isso)`);
      if (motivo === null) return; // Cancelou
    } else {
      if (!window.confirm(`Mudar status de ${p.nome} para: ${novoStatus}?`)) return;
    }

    try {
      const updateData = {
        statusRH: novoStatus,
        motivoReprovacao: motivo || ''
      };
      if (novoStatus === 'Aprovado') {
        updateData.aprovadoEm = new Date().toISOString();
      }
      await updateDoc(doc(db, 'profissionais_parceiros', p.id), updateData);
    } catch (err) {
      console.error(err);
      alert('Erro ao atualizar status.');
    }
  };

  const formatBRDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleBlock = async (p) => {
    const motivo = window.prompt(`🚫 Solicitação de Bloqueio GLOBAL para ${p.nome} (CPF: ${p.cpf || 'N/A'}).\nInforme o motivo deste bloqueio severo:`);
    if (!motivo) return;

    try {
      await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
        statusRH: 'Bloqueado',
        statusTI: 'Bloqueado',
        motivoBloqueio: motivo,
        bloqueadoPor: currentUser.email,
        bloqueadoEm: new Date().toISOString()
      });
      alert('Profissional inserido na Blacklist com sucesso.');
    } catch (err) {
      console.error(err);
      alert('Erro ao bloquear profissional.');
    }
  };

  const handleDesbloquear = async (p) => {
    if (!window.confirm(`Tem certeza que deseja DESBLOQUEAR ${p.nome} e enviá-lo novamente para análise?`)) return;

    try {
      await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
        statusRH: 'Aguardando',
        statusTI: 'Pendente',
        motivoBloqueio: '',
        bloqueadoPor: '',
        bloqueadoEm: ''
      });
      alert('Profissional desbloqueado e retornado para a fila de Aguardando Aprovação.');
    } catch (err) {
      console.error(err);
      alert('Erro ao desbloquear profissional.');
    }
  };

  const handleLiberacaoTemporaria = async (p) => {
    const ls = getLiberacaoStatus(p);
    if (ls || p.liberacaoTemporaria) {
      if (!window.confirm(`Revogar liberação temporária de ${p.nome}?\nEle não poderá mais ser escalado sem documentação regular.`)) return;
      try {
        await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
          liberacaoTemporaria: false,
          liberacaoTemporariaEm: '',
          liberacaoTemporariaPor: ''
        });
      } catch (err) { console.error(err); alert('Erro ao revogar liberação.'); }
    } else {
      if (!window.confirm(`Conceder LIBERAÇÃO TEMPORÁRIA de 30 dias para ${p.nome}?\nEle poderá ser escalado mesmo com documentação pendente/vencida.`)) return;
      try {
        await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
          liberacaoTemporaria: true,
          liberacaoTemporariaEm: new Date().toISOString(),
          liberacaoTemporariaPor: currentUser.email
        });
        alert(`Liberação temporária concedida. A cooperativa tem 30 dias para regularizar a documentação de ${p.nome}.`);
      } catch (err) { console.error(err); alert('Erro ao conceder liberação.'); }
    }
  };

  const s = {
    page: { padding: '24px', fontFamily: "'Inter', sans-serif", background: '#f8fafc', minHeight: '100vh', color: '#1e293b' },
    header: { marginBottom: '24px' },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' },
    tab: (active) => ({ padding: '8px 16px', cursor: 'pointer', fontWeight: 600, borderBottom: active ? '3px solid #3b82f6' : '3px solid transparent', color: active ? '#3b82f6' : '#64748b' }),
    card: { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '16px' },
    th: { textAlign: 'left', padding: '12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '0.85rem' },
    td: { padding: '12px', borderBottom: '1px solid #e2e8f0', fontSize: '0.9rem', verticalAlign: 'top' },
    btnIcon: { background: 'transparent', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', marginRight: '8px', marginBottom: '8px', display: 'inline-block' },
    btn: { background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 },
    badge: (status) => ({ padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700,
      background: status === 'Aprovado' ? '#dcfce7' : status === 'Reprovado' || status === 'Bloqueado' ? '#fee2e2' : '#fef9c3',
      color: status === 'Aprovado' ? '#166534' : status === 'Reprovado' || status === 'Bloqueado' ? '#991b1b' : '#854d0e',
      display: 'inline-block', marginBottom: '4px'
    }),
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', padding: '24px', borderRadius: '12px', width: '600px' },
    kpiContainer: { display: 'flex', gap: '16px', marginBottom: '24px' },
    kpiCard: { flex: 1, background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
    kpiValue: { fontSize: '2rem', fontWeight: 800, margin: '8px 0 0', color: '#0f172a' },
    kpiLabel: { fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' },
    validadeCard: (status) => ({
      padding: '12px', borderRadius: '8px', marginBottom: '8px', fontSize: '0.85rem',
      background: status === 'vencido' ? '#fee2e2' : status === 'vencendo' ? '#fef9c3' : '#f1f5f9',
      border: `1px solid ${status === 'vencido' ? '#f87171' : status === 'vencendo' ? '#facc15' : '#e2e8f0'}`,
      color: status === 'vencido' ? '#991b1b' : status === 'vencendo' ? '#854d0e' : '#475569'
    })
  };

  // KPIs
  const countAguardando = profissionais.filter(p => p.statusRH === 'Aguardando' || p.statusRH === 'Pendente Doc').length;
  const countAprovados = profissionais.filter(p => p.statusRH === 'Aprovado').length;
  const countRecusados = profissionais.filter(p => p.statusRH === 'Reprovado' || p.statusRH === 'Bloqueado').length;
  const countLiberacaoAtiva = profissionais.filter(p => { const ls = getLiberacaoStatus(p); return ls?.ativa; }).length;
  const countLiberacaoVencida = profissionais.filter(p => { const ls = getLiberacaoStatus(p); return ls && !ls.ativa; }).length;

  const especialidades = [...new Set(profissionais.map(p => p.tipo).filter(Boolean))].sort();
  const cooperativas = [...new Set(profissionais.map(p => p.cooperativa).filter(Boolean))].sort();

  const applyFilters = (lista) => lista.filter(p => {
    const matchNome = !filterNome || (p.nome || '').toLowerCase().includes(filterNome.toLowerCase());
    const matchEsp = !filterEspecialidade || p.tipo === filterEspecialidade;
    const matchCoop = !filterCooperativa || p.cooperativa === filterCooperativa;
    return matchNome && matchEsp && matchCoop;
  });

  const DOC_LABELS = {
    carteiraConselho: "Conselho",
    certidaoNegativa: "Cert. Negativa",
    carteiraVacinacao: "Vacinação",
    diploma: "Diploma",
    carimbo: "Carimbo"
  };

  const getValidadeStatus = (validade) => {
    if (!validade) return null;
    const hoje = new Date();
    const dv = new Date(validade + 'T00:00:00');
    const trintaDias = new Date();
    trintaDias.setDate(hoje.getDate() + 30);
    if (dv < hoje) return 'vencido';
    if (dv < trintaDias) return 'vencendo';
    return 'ok';
  };


  const validadeCellStyle = (status) => ({
    padding: '6px 8px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700,
    textAlign: 'center', lineHeight: '1.3',
    background: status === 'vencido' ? '#fee2e2' : status === 'vencendo' ? '#fef9c3' : status === 'ok' ? '#f0fdf4' : '#f8fafc',
    color: status === 'vencido' ? '#991b1b' : status === 'vencendo' ? '#854d0e' : status === 'ok' ? '#166534' : '#94a3b8',
    border: `1px solid ${status === 'vencido' ? '#fca5a5' : status === 'vencendo' ? '#fde68a' : status === 'ok' ? '#bbf7d0' : '#e2e8f0'}`
  });

  const renderTable = (lista) => (
    <div style={{ overflowX: 'auto' }}>
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Profissional</th>
          <th style={s.th}>Cooperativa</th>
          {Object.values(DOC_LABELS).map(label => (
            <th key={label} style={{...s.th, textAlign: 'center', fontSize: '0.7rem', minWidth: '90px'}}>{label}</th>
          ))}
          <th style={s.th}>Status</th>
          <th style={s.th}>Ações</th>
        </tr>
      </thead>
      <tbody>
        {lista.map(p => {
          const docsObj = p.documentos && !Array.isArray(p.documentos) ? p.documentos : {};
          return (
            <tr key={p.id}>
              <td style={s.td}>
                <strong>{p.nome}</strong><br/>
                <span style={{fontSize: '0.8rem', color: '#64748b'}}>{p.tipo}</span><br/>
                <span style={{fontSize: '0.8rem', color: '#64748b'}}>CPF: {p.cpf || 'N/I'}</span><br/>
                <div style={{marginTop: '4px', fontSize: '0.75rem', color: '#475569'}}>
                  <strong>Solicitado:</strong> {formatBRDate(p.criadoEm)}
                  {p.aprovadoEm && <div><strong>Aprovado:</strong> {formatBRDate(p.aprovadoEm)}</div>}
                </div>
              </td>
              <td style={s.td}>
                <span style={{fontSize: '0.85rem'}}>{p.cooperativa || '-'}</span><br/>
                <span style={{fontSize: '0.75rem', color: '#94a3b8'}}>{p.cooperativaEmail}</span>
              </td>
              {Object.keys(DOC_LABELS).map(docKey => {
                const docItem = docsObj[docKey];
                const validade = docItem?.validade;
                const status = getValidadeStatus(validade);
                const hasDueDate = docKey !== 'diploma';
                return (
                  <td key={docKey} style={{...s.td, textAlign: 'center', padding: '8px 4px'}}>
                    {docItem ? (
                      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <div style={{display: 'flex', gap: '4px'}}>
                          {docItem.url && <ArquivoLink href={docItem.url} target="_blank" rel="noreferrer" style={{fontSize: '0.7rem', color: '#3b82f6', textDecoration: 'none', border: '1px solid #bfdbfe', padding: '2px 4px', borderRadius: '4px', background: '#eff6ff'}}>📄 1</ArquivoLink>}
                          {docItem.url2 && <ArquivoLink href={docItem.url2} target="_blank" rel="noreferrer" style={{fontSize: '0.7rem', color: '#3b82f6', textDecoration: 'none', border: '1px solid #bfdbfe', padding: '2px 4px', borderRadius: '4px', background: '#eff6ff'}}>📄 2</ArquivoLink>}
                        </div>
                        {hasDueDate ? (
                          <>
                            {validade ? (
                              <div style={{...validadeCellStyle(status), marginTop: '4px'}}>
                                {new Date(validade + 'T00:00:00').toLocaleDateString('pt-BR')}
                                <div style={{fontSize: '0.65rem', marginTop: '2px'}}>
                                  {status === 'vencido' ? '❌ Vencido' : status === 'vencendo' ? '⚠️ Vencendo' : '✅ Em dia'}
                                </div>
                              </div>
                            ) : (
                              <div style={{...validadeCellStyle(null), marginTop: '4px'}}>Sem data</div>
                            )}
                            <input
                              type="date"
                              value={validade || ''}
                              onChange={async (e) => {
                                try {
                                  await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
                                    [`documentos.${docKey}.validade`]: e.target.value
                                  });
                                } catch (err) { console.error(err); }
                              }}
                              style={{
                                width: '100%', marginTop: '4px', padding: '2px 4px',
                                fontSize: '0.65rem', border: '1px solid #e2e8f0',
                                borderRadius: '4px', background: '#f8fafc', cursor: 'pointer'
                              }}
                              title="Editar validade"
                            />
                          </>
                        ) : (
                          <div style={{...validadeCellStyle('ok'), marginTop: '4px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0'}}>Sem validade</div>
                        )}
                      </div>
                    ) : (
                      <div style={{...validadeCellStyle(null)}}>—</div>
                    )}
                  </td>
                );
              })}
              <td style={s.td}>
                <span style={s.badge(p.statusRH)}>RH: {p.statusRH}</span><br/>
                {p.statusRH === 'Aprovado' && (
                  <span style={{...s.badge(p.statusTI), marginTop: '4px'}}>TI: {p.statusTI === 'Criado' ? '✅ Criado' : '⏳ Aguardando'}</span>
                )}
                {p.statusRH === 'Reprovado' && <div style={{fontSize: '0.75rem', color: '#991b1b', marginTop: '4px'}}>Motivo: {p.motivoReprovacao}</div>}
                {p.statusRH === 'Bloqueado' && <div style={{fontSize: '0.75rem', color: '#991b1b', fontWeight: 600, marginTop: '4px'}}>Bloqueado: {p.motivoBloqueio}</div>}
                {(() => {
                  const ls = getLiberacaoStatus(p);
                  if (!ls) return null;
                  return (
                    <div style={{marginTop: '6px', padding: '4px 8px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700,
                      background: ls.ativa ? '#f3e8ff' : '#fee2e2',
                      color: ls.ativa ? '#6b21a8' : '#991b1b',
                      border: `1px solid ${ls.ativa ? '#d8b4fe' : '#fca5a5'}`}}>
                      {ls.ativa ? `🟣 Lib. Temp: ${ls.diasRestantes}d restantes` : `⚠️ Lib. Temp VENCIDA (${Math.abs(ls.diasRestantes)}d)`}
                    </div>
                  );
                })()}
              </td>
              <td style={s.td}>
                {(p.statusRH === 'Aguardando' || p.statusRH === 'Pendente Doc') && (
                  <>
                    <button style={{...s.btnIcon, color: '#166534', borderColor: '#166534'}} onClick={() => handleMudarStatusRH(p, 'Aprovado')}>✅</button>
                    <button style={{...s.btnIcon, color: '#991b1b', borderColor: '#991b1b'}} onClick={() => handleMudarStatusRH(p, 'Reprovado')}>❌</button>
                    <button style={{...s.btnIcon, color: '#854d0e', borderColor: '#854d0e'}} onClick={() => handleMudarStatusRH(p, 'Pendente Doc')}>⚠️</button>
                  </>
                )}
                {p.statusRH === 'Reprovado' && (
                  <button
                    style={{
                      ...s.btnIcon,
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      borderColor: '#93c5fd',
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '0.72rem',
                      whiteSpace: 'nowrap',
                      padding: '4px 8px'
                    }}
                    title="Revogar reprovação e retornar para fila de análise"
                    onClick={async () => {
                      if (!window.confirm(`Revogar a reprovação de ${p.nome}?\n\nO profissional voltará para a fila de análise (Aguardando) e o motivo de reprovação será limpo.`)) return;
                      try {
                        await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
                          statusRH: 'Aguardando',
                          motivoReprovacao: '',
                          dataRetornoAnalise: new Date().toISOString(),
                        });
                      } catch (err) {
                        alert('Erro ao revogar reprovação: ' + err.message);
                      }
                    }}
                  >
                    ↩️ Revogar
                  </button>
                )}
                {p.statusRH !== 'Bloqueado' && (
                  <button
                    style={{...s.btnIcon, background: p.liberacaoTemporaria ? '#f3e8ff' : '#faf5ff', color: '#7c3aed', borderColor: '#d8b4fe', display: 'block', marginBottom: '4px'}}
                    onClick={() => handleLiberacaoTemporaria(p)}
                    title={p.liberacaoTemporaria ? 'Revogar Liberação Temporária' : 'Conceder Liberação Temporária'}
                  >
                    {p.liberacaoTemporaria ? '🟣 Revogar Lib.' : '🟣 Lib. Temp.'}
                  </button>
                )}
                {p.statusRH !== 'Bloqueado' ? (
                  <button style={{...s.btnIcon, background: '#fee2e2', color: '#991b1b', borderColor: '#f87171', display: 'block'}} onClick={() => handleBlock(p)}>🚫</button>
                ) : (
                  <button style={{...s.btnIcon, background: '#dcfce7', color: '#166534', borderColor: '#4ade80', display: 'block'}} onClick={() => handleDesbloquear(p)}>✔️</button>
                )}
              </td>
            </tr>
          );
        })}
        {lista.length === 0 && (
          <tr><td colSpan={7} style={{...s.td, textAlign: 'center'}}>Nenhum profissional nesta lista.</td></tr>
        )}
      </tbody>
    </table>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>📋 Avaliação de Profissionais (RH)</h1>
        <p style={{ color: '#64748b', margin: '4px 0 0' }}>Analise os cadastros, aprove, reprove ou aplique bloqueios de segurança.</p>
      </div>

      <div style={s.kpiContainer}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Fila de Análise</div>
          <div style={s.kpiValue}>{countAguardando}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Aprovados</div>
          <div style={{...s.kpiValue, color: '#16a34a'}}>{countAprovados}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Recusados / Blacklist</div>
          <div style={{...s.kpiValue, color: '#dc2626'}}>{countRecusados}</div>
        </div>
        <div style={{...s.kpiCard, background: '#faf5ff', border: '1px solid #e9d5ff'}}>
          <div style={{...s.kpiLabel, color: '#7c3aed'}}>Lib. Temp. Ativa</div>
          <div style={{...s.kpiValue, color: '#7c3aed'}}>{countLiberacaoAtiva}</div>
          {countLiberacaoVencida > 0 && (
            <div style={{fontSize: '0.75rem', color: '#dc2626', fontWeight: 700, marginTop: '4px'}}>
              ⚠️ {countLiberacaoVencida} vencida(s)
            </div>
          )}
        </div>
      </div>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <input
          type="text"
          placeholder="🔍 Nome do profissional..."
          value={filterNome}
          onChange={e => setFilterNome(e.target.value)}
          style={{ flex: '1 1 200px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none' }}
        />
        <select
          value={filterEspecialidade}
          onChange={e => setFilterEspecialidade(e.target.value)}
          style={{ flex: '1 1 180px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', background: '#fff' }}
        >
          <option value="">Todas especialidades</option>
          {especialidades.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select
          value={filterCooperativa}
          onChange={e => setFilterCooperativa(e.target.value)}
          style={{ flex: '1 1 180px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', background: '#fff' }}
        >
          <option value="">Todas cooperativas</option>
          {cooperativas.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(filterNome || filterEspecialidade || filterCooperativa) && (
          <button
            onClick={() => { setFilterNome(''); setFilterEspecialidade(''); setFilterCooperativa(''); }}
            style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
          >
            ✕ Limpar
          </button>
        )}
      </div>

      <div style={s.tabs}>
        <div style={s.tab(activeTab === 'aguardando')} onClick={() => setActiveTab('aguardando')}>Aguardando Aprovação</div>
        <div style={s.tab(activeTab === 'aprovados')} onClick={() => setActiveTab('aprovados')}>Aprovados</div>
        <div style={s.tab(activeTab === 'recusados')} onClick={() => setActiveTab('recusados')}>Recusados / Bloqueados</div>
        <div style={s.tab(activeTab === 'validades')} onClick={() => setActiveTab('validades')}>⚠️ Validades</div>
        <div style={{...s.tab(activeTab === 'lib_temp'), color: activeTab === 'lib_temp' ? '#7c3aed' : undefined, borderBottom: activeTab === 'lib_temp' ? '3px solid #7c3aed' : '3px solid transparent'}} onClick={() => setActiveTab('lib_temp')}>
          🟣 Lib. Temporária {countLiberacaoAtiva + countLiberacaoVencida > 0 && `(${countLiberacaoAtiva + countLiberacaoVencida})`}
        </div>
      </div>

      <div style={s.card}>
        {loading ? <p>Carregando...</p> : (
          activeTab === 'aguardando'
            ? renderTable(applyFilters(profissionais.filter(p => p.statusRH === 'Aguardando' || p.statusRH === 'Pendente Doc')))
            : activeTab === 'aprovados'
            ? renderTable(applyFilters(profissionais.filter(p => p.statusRH === 'Aprovado')))
            : activeTab === 'recusados'
            ? renderTable(applyFilters(profissionais.filter(p => p.statusRH === 'Reprovado' || p.statusRH === 'Bloqueado')))
            : activeTab === 'lib_temp'
            ? renderTable(applyFilters(profissionais.filter(p => p.liberacaoTemporaria)))
            : renderValidades()
        )}
      </div>

      {showDocModal && selectedProf && (
        <div style={s.modalOverlay}>
          <div style={s.modalContent}>
            <h3 style={{marginTop: 0, color: '#0f172a'}}>Documentos de {selectedProf.nome}</h3>
            
            {(!selectedProf.documentos || typeof selectedProf.documentos !== 'object' || Array.isArray(selectedProf.documentos)) ? (
              <div style={{ background: '#f8fafc', padding: '40px 20px', textAlign: 'center', borderRadius: '8px', border: '1px dashed #cbd5e1', marginBottom: '24px' }}>
                <p style={{color: '#94a3b8', margin: 0}}>O novo checklist de documentos não foi anexado. (Legado ou Vazio)</p>
              </div>
            ) : (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '0.9rem', color: '#64748b' }}>Clique no botão para abrir o documento na nuvem.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Object.entries({
                    carteiraConselho: "Carteira do Conselho",
                    certidaoNegativa: "Certidão Negativa",
                    carteiraVacinacao: "Carteira de Vacinação",
                    diploma: "Diploma de Graduação"
                  }).map(([key, label]) => {
                    const docItem = selectedProf.documentos[key];
                    return (
                      <div key={key} style={{ background: '#f1f5f9', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong style={{ fontSize: '0.95rem', color: '#334155' }}>{label}</strong>
                          {docItem ? (
                            <span style={{ fontSize: '0.8rem', color: '#16a34a', display: 'block', fontWeight: 600 }}>✅ Anexado</span>
                          ) : (
                            <span style={{ fontSize: '0.8rem', color: '#dc2626', display: 'block', fontWeight: 600 }}>❌ Não enviado</span>
                          )}
                        </div>
                        {docItem && (docItem.url || docItem.url2) && (
                          <div style={{display: 'flex', gap: '8px'}}>
                            {docItem.url && <ArquivoLink href={docItem.url} target="_blank" rel="noreferrer" style={{ background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>Abrir Doc 1</ArquivoLink>}
                            {docItem.url2 && <ArquivoLink href={docItem.url2} target="_blank" rel="noreferrer" style={{ background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>Abrir Doc 2</ArquivoLink>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div style={{display:'flex', justifyContent:'flex-end'}}>
              <button onClick={() => setShowDocModal(false)} style={s.btn}>Fechar Visualização</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderValidades() {
    const hoje = new Date();
    const trintaDias = new Date();
    trintaDias.setDate(hoje.getDate() + 30);

    const profsComDocs = profissionais.filter(p => p.documentos && !Array.isArray(p.documentos));
    
    const filteredProfs = profsComDocs.filter(p => {
      const docs = p.documentos;
      const docKeys = Object.keys(DOC_LABELS).filter(k => k !== 'diploma');
      if (validadeFilter === 'todos') return docKeys.some(k => docs[k]?.validade);
      if (validadeFilter === 'vencidos') return docKeys.some(k => { const v = docs[k]?.validade; return v && new Date(v + 'T00:00:00') < hoje; });
      if (validadeFilter === 'vencendo') return docKeys.some(k => { const v = docs[k]?.validade; return v && new Date(v + 'T00:00:00') >= hoje && new Date(v + 'T00:00:00') < trintaDias; });
      if (validadeFilter === 'vacinas') return !!docs.carteiraVacinacao?.validade;
      return false;
    });

    return (
      <div>
        <div style={{display: 'flex', gap: '12px', marginBottom: '24px'}}>
          <button style={{...s.btnIcon, background: validadeFilter === 'todos' ? '#3b82f6' : '#fff', color: validadeFilter === 'todos' ? '#fff' : '#64748b'}} onClick={() => setValidadeFilter('todos')}>Todos</button>
          <button style={{...s.btnIcon, background: validadeFilter === 'vencidos' ? '#ef4444' : '#fff', color: validadeFilter === 'vencidos' ? '#fff' : '#64748b'}} onClick={() => setValidadeFilter('vencidos')}>🔴 Vencidos</button>
          <button style={{...s.btnIcon, background: validadeFilter === 'vencendo' ? '#f59e0b' : '#fff', color: validadeFilter === 'vencendo' ? '#fff' : '#64748b'}} onClick={() => setValidadeFilter('vencendo')}>🟡 Vencendo</button>
          <button style={{...s.btnIcon, background: validadeFilter === 'vacinas' ? '#10b981' : '#fff', color: validadeFilter === 'vacinas' ? '#fff' : '#64748b'}} onClick={() => setValidadeFilter('vacinas')}>💉 Vacinas</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Profissional</th>
              <th style={s.th}>Cooperativa</th>
              <th style={s.th}>Status</th>
              {Object.values(DOC_LABELS).map(label => (
                <th key={label} style={{...s.th, textAlign: 'center', fontSize: '0.7rem', minWidth: '100px'}}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredProfs.map(p => {
              const docs = p.documentos;
              return (
                <tr key={p.id}>
                  <td style={s.td}>
                    <strong>{p.nome}</strong><br/>
                    <span style={{fontSize: '0.78rem', color: '#64748b'}}>{p.tipo}</span>
                  </td>
                  <td style={s.td}><span style={{fontSize: '0.85rem'}}>{p.cooperativa || '-'}</span></td>
                  <td style={s.td}><span style={s.badge(p.statusRH)}>{p.statusRH}</span></td>
                  {Object.keys(DOC_LABELS).map(docKey => {
                    const validade = docs[docKey]?.validade;
                    const status = getValidadeStatus(validade);
                    const hasDueDate = docKey !== 'diploma';
                    return (
                      <td key={docKey} style={{...s.td, textAlign: 'center', padding: '8px 4px'}}>
                        {hasDueDate ? (
                          validade ? (
                            <div style={validadeCellStyle(status)}>
                              {new Date(validade + 'T00:00:00').toLocaleDateString('pt-BR')}
                              <div style={{fontSize: '0.65rem', marginTop: '2px'}}>
                                {status === 'vencido' ? '❌ Vencido' : status === 'vencendo' ? '⚠️ Vencendo' : '✅ Em dia'}
                              </div>
                            </div>
                          ) : (
                            <div style={{...validadeCellStyle(null)}}>—</div>
                          )
                        ) : (
                          <div style={{...validadeCellStyle('ok'), background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0'}}>Sem validade</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {filteredProfs.length === 0 && (
              <tr><td colSpan={7} style={{...s.td, textAlign: 'center', padding: '40px', color: '#94a3b8'}}>Nenhum documento com validade encontrado.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    );
  }
}
